import { getMatchScore } from '../../Config/Tiles';
import { Suits } from '../../Tiles/TileSet';
import { BoardManagerUtils } from '../Board/BoardManagerUtils';
import { IGameState } from '../../State/Store/store';
import { getRandom } from '../../Utils/Utils';
import { IStateLevel } from '../../State/reducers/level';
import { ItemIDs } from '../../Config/Items';
import { getBoosterActive, IStateBoosters } from '../../State/reducers/boosters';
import { IStateBoard } from '../../State/reducers/board';
import { waitForMs } from '../../Utils/AsyncUtils';
import { IBoardMap } from '../../Game/BoardArrangement';

export class BoosterUtils {
    public static scoreMagnetMatch(id: string, i: number, m: number): number {
        let score = 0;
        if (i % 2 === 0) {
            score = getMatchScore(id as Suits) * m;
        }

        return score;
    }

    public static startMagnetPull(state: IGameState, count: (...args: Array<unknown>) => void) {
        return pulls(state, count);
    }

    public static startTileBursts(
        state: IGameState,
        boardMap: IBoardMap,
        count: (...args: any) => void
    ) {
        return bursts(state, boardMap, count);
    }

    public static checkBoosterInGameUnlock(state: IStateLevel): ItemIDs {
        if (state.levelIndex === 15) {
            return ItemIDs.SHUFFLE;
        }

        if (state.levelIndex === 30) {
            return ItemIDs.TIME;
        }

        return null;
    }

    public static getBoostersToRemove(state: IStateBoosters, previous: IStateBoosters): ItemIDs[] {
        const removes: ItemIDs[] = [];
        if (state.currentBoosters.length !== previous.currentBoosters.length) {
            [ItemIDs.MAGNET, ItemIDs.FIRECRACKER, ItemIDs.HINT, ItemIDs.HIGHLIGHT].forEach(key => {
                if (
                    !state.currentBoosters.includes(key) &&
                    previous.currentBoosters.includes(key)
                ) {
                    removes.push(key);
                }
            });
        }

        return removes;
    }

    public static canAddBooster(boosters: IStateBoosters, id: string, running: string[]): boolean {
        const inCurrent = boosters.currentBoosters.includes(id as ItemIDs);
        const hasConflict = BoosterUtils.checkBoosterConficts(id, running);
        return !inCurrent && !hasConflict;
    }

    public static canSelectBooster(state: IGameState, id: string, running: string[]): boolean {
        const { StateBoosters, StateBoard } = state;
        const active = getBoosterActive(state);
        const inCurrent = StateBoosters.currentBoosters.includes(id as ItemIDs);
        if ((id as ItemIDs) === ItemIDs.MAGNET && active && inCurrent) {
            // allow magnet if active so it can be cancel
            return true;
        }
        const enabled = BoosterUtils.checkBoosterEnabled(StateBoosters, StateBoard, id as ItemIDs);
        const canAdd = BoosterUtils.canAddBooster(StateBoosters, id, running);
        return enabled && active && canAdd;
    }

    public static isTutorialBooster(boosters: IStateBoosters, id: ItemIDs) {
        return boosters.freeBoosters.includes(id) && !boosters.currentBoosters.includes(id);
    }

    public static checkBoosterConficts(id: string, running: string[]): boolean {
        return (id === ItemIDs.FIRECRACKER || id === ItemIDs.MAGNET) && running.length > 0;
    }

    public static getAddedBoosters(
        state: IStateBoosters,
        previous: IStateBoosters,
        collectionKey: string
    ): ItemIDs[] {
        const waiting: ItemIDs[] = [];

        [
            ItemIDs.MAGNET,
            ItemIDs.FIRECRACKER,
            ItemIDs.TIME,
            ItemIDs.HIGHLIGHT,
            ItemIDs.SHUFFLE,
        ].forEach(key => {
            const pB = (previous[collectionKey] as ItemIDs[]).filter((x: any) => x.includes(key))
                .length;
            const sB = (state[collectionKey] as ItemIDs[]).filter((x: any) => x.includes(key))
                .length;

            if (sB > 0 && sB > pB) {
                waiting.push(key);
            }
        });

        return waiting;
    }

    public static checkBoosterEnabled(
        boosters: IStateBoosters,
        board: IStateBoard,
        id: ItemIDs
    ): boolean {
        const activating =
            boosters.waitingBoosters.filter(x => x !== ItemIDs.MAGNET && x !== ItemIDs.HINT)
                .length > 0;
        const currentBoosters = boosters.currentBoosters.filter(
            x => x !== ItemIDs.HIGHLIGHT && x !== ItemIDs.HINT
        );

        return !(
            activating ||
            currentBoosters.length > 0 ||
            !board.ready ||
            boosters.lockedBoosters.includes(id)
        );
    }

    public static checkAutoBooster(id: ItemIDs, levelIndex: number) {
        if (
            ((levelIndex <= 8 || levelIndex === 18) && id === ItemIDs.HIGHLIGHT) ||
            (levelIndex <= 2 && id === ItemIDs.HINT)
        ) {
            return true;
        }

        return false;
    }
}

async function* pulls(state: IGameState, count: (...args: Array<unknown>) => void): any {
    const {
        StateBoard,
        StateScore,
        StateLevel: { removes, selected },
    } = state;

    let i = 0;
    while (i < removes.length) {
        const id = removes[i];
        const score = BoosterUtils.scoreMagnetMatch(id, i, StateScore.multiplier);
        const magnetScore = BoardManagerUtils.getScoreForSuit(StateBoard.boardMap, id);
        const magnetId = selected[0];
        const len = removes.length;
        const delay = 800 / len;
        const pullDelay = delay * i;
        await waitForMs(pullDelay);
        count(magnetId, magnetScore, id, score, pullDelay, i < len - 1);

        i++;
        yield i;
    }

    await waitForMs(850);
    return 'complete';
}

async function bursts(
    state: IGameState,
    boardMap: IBoardMap,
    count: (...args: Array<unknown>) => void
): Promise<void> {
    const {
        StateScore: { multiplier },
        StateLevel: { removes: levelRemoves },
    } = state;
    const removes = [...new Set(levelRemoves)];
    let i = 0;
    while (i < removes.length) {
        const randomTime = getRandom(100, 200);

        const id = removes[i];
        const tile = boardMap[id];
        if (!tile) {
            return;
        }
        const suit = tile.tileType.suit as Suits;
        let score = 0;
        if (i % 2 === 0) {
            score = getMatchScore(suit) * multiplier;
        }

        await waitForMs(randomTime);
        count(i, id, score);
        i++;
    }
}
